import { Button, Sheet, SheetBody, SheetFooter, SheetHeader, TextField } from "@community-project-org/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import { ROLE_LABELS, type TenantRole } from "../../common/membership/roles.js";
import type { components } from "../../generated/api.js";
import { ScopePicker } from "./ScopePicker.js";
import { ALL_SCOPE, draftOf, scopeBody, type ScopeDraft } from "./scopeDraft.js";

type TeamMemberDTO = components["schemas"]["TeamMemberDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];
type ServiceDTO = components["schemas"]["ServiceDTO"];

interface MemberSheetProps {
  /** Ohne Mitglied: neu einladen. */
  member: TeamMemberDTO | null;
  roles: TenantRole[];
  groups: ServiceGroupDTO[];
  services: ServiceDTO[];
  onClose: () => void;
}

/**
 * Einladen oder bearbeiten: Mailadresse, Rolle, Umfang. Admins gelten immer für alles —
 * dann zeigt das Blatt den Umfang gar nicht erst an, statt ihn still zu übergehen.
 */
export function MemberSheet({ member, roles, groups, services, onClose }: MemberSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>(member?.role ?? roles[0] ?? "CONTROLLER");
  const [scope, setScope] = useState<ScopeDraft>(member ? draftOf(member) : ALL_SCOPE);
  const alwaysAll = role === "TENANT_ADMIN" || role === "PLATFORM_ADMIN";

  const save = useMutation({
    mutationFn: () =>
      member
        ? apiRequest<TeamMemberDTO>(`/team/${encodeURIComponent(member.membershipId)}`, {
            method: "PATCH",
            body: { role, ...scopeBody(scope) },
          })
        : apiRequest<TeamMemberDTO>("/team", {
            method: "POST",
            body: { email, role, ...scopeBody(scope) },
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["team"] });
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      apiRequest<void>(`/team/${encodeURIComponent(member?.membershipId ?? "")}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["team"] });
      onClose();
    },
  });

  const error = save.error ?? remove.error;

  return (
    <Sheet open onClose={onClose} label={member ? "Teammitglied bearbeiten" : "Ins Team einladen"} size="md">
      <SheetHeader>
        <h2 className="text-[18px] font-extrabold text-ink">{member ? member.displayName : "Ins Team einladen"}</h2>
        {member && <p className="text-[13px] text-ink-3">{member.email}</p>}
      </SheetHeader>
      <SheetBody>
        <form
          id="member-form"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {!member && (
            <TextField
              label="Mailadresse"
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              hint="Die Person meldet sich mit dieser Adresse an; bis dahin steht sie als eingeladen da."
            />
          )}
          <label className="grid gap-1 text-[12px] font-bold text-ink-2">
            Rolle
            <select
              className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink"
              value={role}
              onChange={(event) => setRole(event.target.value as TenantRole)}
            >
              {roles.map((entry) => (
                <option key={entry} value={entry}>
                  {ROLE_LABELS[entry].label}
                </option>
              ))}
            </select>
            <span className="text-[12px] font-normal text-ink-3">{ROLE_LABELS[role].description}</span>
          </label>

          {alwaysAll ? (
            <p className="text-[13px] text-ink-2">Diese Rolle gilt immer für alle Angebote.</p>
          ) : (
            <ScopePicker value={scope} onChange={setScope} groups={groups} services={services} />
          )}

          {error && (
            <p className="text-[13px] text-danger" role="alert">
              {error instanceof ApiRequestError ? error.message : "Das hat nicht geklappt."}
            </p>
          )}
        </form>
      </SheetBody>
      <SheetFooter>
        {member && (
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`${member.displayName} aus dem Team entfernen?`)) {
                remove.mutate();
              }
            }}
          >
            Entfernen
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button type="submit" form="member-form" disabled={save.isPending} aria-busy={save.isPending}>
          {save.isPending ? "Wird gespeichert …" : member ? "Speichern" : "Einladen"}
        </Button>
      </SheetFooter>
    </Sheet>
  );
}
